import React, { useCallback, useContext } from 'react';
import moment from 'moment';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import { getTimeRangeWithDates, isEndTimeBeforeStartTime } from '@ubeya/shared/utils/time';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { isCellClearable, isCellEditable, isReadOnly } from 'shared/utils/timesheet';
import BaseTimeRenderer from './BaseTimeRenderer';

const CheckOutTimeRenderer = ({
  rowData,
  columnData: {
    updateCheckOutTime,
    updateOSCheckOutTime,
    readOnly: inputReadOnly = false,
    allowUpdateExternal = false,
    onOutsideClickSave = false,
    disableWarning = false
  } = {}
}) => {
  const myPrivileges = useContext(PrivilegesContext);
  const formattingMap = useFormattingMap();

  const onChangeCheckOutTime = useCallback(
    ({ checkOutTime }) =>
      !rowData.isExternal
        ? updateCheckOutTime({ newTime: checkOutTime, ...rowData })
        : updateOSCheckOutTime({ newTime: checkOutTime, ...rowData }),
    [rowData, updateCheckOutTime, updateOSCheckOutTime]
  );

  const handleUpdateTimeSheet = useCallback(
    (newTime, originalNewTime) => {
      const { endTime: checkOutTime } = getTimeRangeWithDates({
        date: rowData.date,
        startTime: rowData.actualCheckIn,
        endTime: newTime,
        checkBaseStartDate: true,
        includeEqualStartEndTIme: false
      });

      if (newTime) {
        if (!moment(checkOutTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualCheckOut, API_DATE_TIME_FORMAT))) {
          onChangeCheckOutTime({ checkOutTime });
        }
      } else if (originalNewTime === null) {
        onChangeCheckOutTime({ checkOutTime });
      }
    },
    [rowData.date, rowData.actualCheckIn, rowData.actualCheckOut, onChangeCheckOutTime]
  );

  const readOnly = inputReadOnly || !rowData.actualCheckIn;

  const isEditable = isCellEditable({
    readOnly: isReadOnly(rowData, readOnly),
    updateFunc: rowData?.isExternal ? updateOSCheckOutTime : updateCheckOutTime,
    isExternal: rowData?.isExternal,
    allowUpdateExternal
  });

  const isClearable = isCellClearable({
    isClientApproved: rowData?.clientApproved && rowData?.isExternal,
    isApproved: rowData?.approved,
    isUnlockPayrollPrivilege: myPrivileges[privileges.UNLOCK_PAYROLL],
    allowClear: true
  });

  const extraDay =
    rowData.actualCheckIn &&
    rowData.actualCheckOut &&
    isEndTimeBeforeStartTime(moment(rowData.actualCheckIn), moment(rowData.actualCheckOut), false);

  return (
    <>
      <BaseTimeRenderer
        isIn
        disableWarning={disableWarning}
        tooltipBody={rowData.actualCheckOut && formattingMap.dateAndTime(rowData.actualCheckOut)}
        rowData={rowData}
        startTime={rowData.actualCheckIn}
        actualValue={rowData.actualCheckOut}
        actualUserValue={rowData.userActualCheckOut}
        actualReporter={rowData.actualCheckOutReporter}
        reportMethod={rowData.reportMethods?.checkOut}
        reportedValueMethod={rowData.reportMethods?.reportedCheckOut}
        scheduleValue={rowData.scheduleOut}
        onChangeActualValue={handleUpdateTimeSheet}
        onOutsideClickSave={onOutsideClickSave}
        isEditable={isEditable}
        clearable={isClearable}
        extraDay={extraDay}
      />
    </>
  );
};

export default CheckOutTimeRenderer;
